import { useState } from "react";

import { useToast } from "@hightouch/htui";
import { useQueryClient } from "react-query";

import {
  buildLineageExportSheets,
  type ExportEdge,
  type ExportNode,
} from "src/components/lineage/graph-export";
import { buildLineageExportFilename } from "src/components/lineage/lineage-export";
import { isExpiredCursor } from "src/components/lineage/lineage-pagination";
import { useLineageResourceUrl } from "src/components/lineage/use-lineage-resource-url";
import { loadResourceDependentsPage } from "src/components/lineage/usage-panel/resource-dependents-page-loader";
import { buildResourceUsageRows } from "src/components/lineage/usage-panel/usage-export";
import * as Sentry from "src/lib/monitoring";
import { downloadCsv, downloadXlsx } from "src/utils/download";

const UNEXPECTED_ERROR =
  "Something went wrong on our end. Try again, or contact support if it keeps happening.";

type ResourceUsageExport = {
  kind: "resource";
  resourceType: string;
  resourceId: string;
  /** The snapshot the caller is showing, so the file matches what is on screen. */
  snapshotToken: string;
  /** Called when the snapshot is replaced before the export finishes reading it. */
  onSnapshotExpired?: () => void;
};

type WorkspaceGraphExport = {
  kind: "workspace";
  workspaceName: string | undefined;
  nodes: ExportNode[];
  edges: ExportEdge[];
};

export type LineageExportTarget = ResourceUsageExport | WorkspaceGraphExport;

/**
 * Builds and downloads a lineage export: a CSV of everything downstream of one
 * resource, or a workbook of the whole workspace graph. Every export shares the
 * in-progress state, links, file naming, error reporting, and failure toasts.
 */
export function useLineageExport(): {
  isExporting: boolean;
  exportLineage: (target: LineageExportTarget) => Promise<void>;
} {
  const queryClient = useQueryClient();
  const resourceUrl = useLineageResourceUrl();
  const { toast } = useToast();
  const [isExporting, setIsExporting] = useState(false);

  const downloadResourceUsage = async ({
    resourceType,
    resourceId,
    snapshotToken,
  }: ResourceUsageExport) => {
    const rows = await buildResourceUsageRows({
      root: { resourceType, resourceId },
      resourceUrl,
      loadPage: async (args) => {
        const result = await loadResourceDependentsPage({
          queryClient,
          ...args,
          snapshotToken,
        });
        const { rows, pageInfo } = result.resourceDependentsPage;
        if (!rows) {
          throw new Error("pinned lineage snapshot returned no rows");
        }
        return { rows, pageInfo };
      },
    });
    downloadCsv(
      rows,
      buildLineageExportFilename({
        subject: `${resourceType}_${resourceId}`,
        kind: "usage",
        extension: "csv",
        exportedAt: new Date(),
      }),
    );
  };

  const downloadWorkspaceGraph = async ({
    workspaceName,
    nodes,
    edges,
  }: WorkspaceGraphExport) => {
    // The whole graph is walked from every node, so hand the spinner a frame
    // to paint before the main thread commits to the walk.
    await new Promise((resolve) => setTimeout(resolve, 0));
    const sheets = await buildLineageExportSheets({
      nodes,
      edges,
      resourceUrl,
    });
    await downloadXlsx(
      sheets,
      buildLineageExportFilename({
        subject: workspaceName ?? "workspace",
        kind: "lineage-graph",
        extension: "xlsx",
        exportedAt: new Date(),
      }),
    );
  };

  const reportFailure = (target: LineageExportTarget, error: unknown) => {
    if (target.kind === "workspace") {
      Sentry.captureException(error);
      toast({
        id: "lineage-graph-export",
        title: "Couldn't export the lineage graph",
        message: "Try again, or check the browser console for details.",
        variant: "error",
      });
      return;
    }

    if (isExpiredCursor(error)) {
      target.onSnapshotExpired?.();
      toast({
        id: "usage-export",
        title: "Couldn’t download the CSV",
        message:
          "Usage was recalculated while the file was being built. Reload to see the latest usage, then try again.",
        variant: "warning",
      });
      return;
    }

    Sentry.captureException(error);
    toast({
      id: "usage-export",
      title: "Couldn’t download the CSV",
      message: UNEXPECTED_ERROR,
      variant: "error",
    });
  };

  const exportLineage = async (target: LineageExportTarget) => {
    if (isExporting) {
      return;
    }
    setIsExporting(true);
    try {
      if (target.kind === "resource") {
        await downloadResourceUsage(target);
      } else {
        await downloadWorkspaceGraph(target);
      }
    } catch (error) {
      reportFailure(target, error);
    } finally {
      setIsExporting(false);
    }
  };

  return { isExporting, exportLineage };
}
